// CommonSearch.js
/**
 * CommonSearch - A reusable search component with autocomplete functionality
 * 
 * Features:
 * - Real-time search with debounced API calls
 * - Autocomplete with result type indicators
 * - Support for custom search filters
 * - Customizable result selection handling
 * - Loading states and error handling
 * - Handles new grouped results format from BookMyOP Search API
 * 
 * Props:
 * @param {string} placeholder - Placeholder text for the search input
 * @param {function} onSearch - Callback when search is performed (query, results)
 * @param {function} onResultSelect - Callback when a result is selected
 * @param {object} searchFilters - Additional filters to apply to search
 * @param {string} maxWidth - Maximum width of the container
 * @param {boolean} showResultTypes - Whether to show result type chips
 * @param {boolean} showFilters - Whether to show filter options (future feature)
 * @param {object} sx - Additional Material-UI sx styles
 * 
 * API Response Format:
 * The component now handles the new grouped results format and sorts results in priority order:
 * {
 *   "status": "success",
 *   "data": {
 *     "grouped_results": {
 *       "doctor": { "type": "doctors", "title": "Doctors", "count": 50, "results": [...] },
 *       "service_provider": { "type": "service_providers", "title": "Hospitals & Clinics", "count": 13, "results": [...] },
 *       "specialty": { "type": "specialties", "title": "Specialties", "count": 1, "results": [...] }
 *     }
 *   }
 * }
 * 
 * Result Display Order: Specialties → Doctors → Service Providers
 * 
 * Usage Examples:
 * 
 * // Basic usage
 * <CommonSearch placeholder="Search..." />
 * 
 * // With custom filters
 * <CommonSearch 
 *   searchFilters={{ result_types: ['doctor'], city: 'Mumbai' }}
 *   onResultSelect={(result) => console.log('Selected:', result)}
 * />
 * 
 * // With custom search handler
 * <CommonSearch 
 *   onSearch={(query, results) => {
 *     console.log('Search performed:', query);
 *     console.log('Results:', results);
 *   }}
 * />
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Paper,
  IconButton,
  useTheme,
  alpha,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../hooks/useDebounce';
import BookMyOpAPI from '../services/BookMyOpapi';

const CommonSearch = ({
  placeholder = "Search Doctors, Hospitals and more...",
  onSearch,
  onResultSelect,
  searchFilters = {},
  maxWidth = "xxl",
  showResultTypes = true,
  showFilters = false,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  // Ref to track the last search query to prevent duplicate API calls
  const lastSearchQuery = useRef('');
  const lastSearchTime = useRef(0);
  const SEARCH_COOLDOWN = 300; // 300ms cooldown between searches (reduced for better UX)

  // Memoize searchFilters to prevent unnecessary re-renders
  const memoizedSearchFilters = useMemo(() => searchFilters, [JSON.stringify(searchFilters)]);

  // Debounce the input value to avoid too many API calls (like Google search)
  const debouncedInputValue = useDebounce(inputValue, 500, 3); // 500ms delay, min 3 chars

  // Search function with better dependency management
  const performSearch = useCallback(async (query, filters = {}) => {
    if (!query || query.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    // Prevent duplicate searches for the same query
    const trimmedQuery = query.trim();
    if (trimmedQuery === lastSearchQuery.current) {
      return;
    }

    // Rate limiting - prevent searches too frequently
    const now = Date.now();
    if (now - lastSearchTime.current < SEARCH_COOLDOWN) {
      console.log('Search rate limited, skipping...');
      return;
    }

    lastSearchQuery.current = trimmedQuery;
    lastSearchTime.current = now;

    setIsLoading(true);
    setError(null);

    try {
      const searchParams = {
        query: trimmedQuery,
        page_size: 20,
        ...memoizedSearchFilters,
        ...filters,
      };

      const response = await BookMyOpAPI.search(searchParams);
      console.log('Search API Response:', response);
      
      // Handle new grouped results format from BookMyOP Search API
      // The API returns results grouped by type: doctor, service_provider, specialty (singular keys)
      // Each group contains: type, title, count, and results array
      let results = [];
      if (response.status === 'success' && response.data?.grouped_results) {
        // Extract results from grouped structure and flatten into single array
        // Order: specialties first, then doctors, then service providers
        const groupedResults = response.data.grouped_results;
        results = [];
        
        // Add specialties first (highest priority)
        if (groupedResults.specialty?.results) {
          results.push(...groupedResults.specialty.results);
        }
        
        // Add doctors second
        if (groupedResults.doctor?.results) {
          results.push(...groupedResults.doctor.results);
        }
        
        // Add service providers (hospitals/clinics) last
        if (groupedResults.service_provider?.results) {
          results.push(...groupedResults.service_provider.results);
        }
      } else if (response.status === 'success' && response.data?.results) {
        // Fallback for old format
        results = response.data.results;
      } else if (response.results) {
        // Fallback for direct results array
        results = response.results;
      } else if (Array.isArray(response)) {
        // Fallback for direct array response
        results = response;
      }
      
      if (results && results.length > 0) {
        const formattedResults = results.map((result) => ({
          ...result,
          label: getResultLabel(result),
          displayType: getResultTypeLabel(result.result_type),
        }));
        console.log('Formatted Results:', formattedResults);
        setSearchResults(formattedResults);
      } else {
        console.log('No results found in response:', response);
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Search failed');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [memoizedSearchFilters]); // Use memoized searchFilters

  // Effect to trigger search when debounced input changes
  useEffect(() => {
    if (debouncedInputValue && debouncedInputValue.trim().length >= 3) {
      console.log('Triggering search for:', debouncedInputValue);
      performSearch(debouncedInputValue);
    } else {
      setSearchResults([]);
      lastSearchQuery.current = '';
    }
  }, [debouncedInputValue, performSearch]);

  // Debug effect to log searchResults changes
  useEffect(() => {
    console.log('Search results updated:', searchResults);
    // Open dropdown if we have results and input is long enough
    if (searchResults.length > 0 && inputValue.length >= 3) {
      setIsOpen(true);
    }
  }, [searchResults, inputValue]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clear search state when component unmounts
      lastSearchQuery.current = '';
      lastSearchTime.current = 0;
    };
  }, []);

  // Helper function to get result label
  const getResultLabel = (result) => {
    switch (result.result_type) {
      case 'doctor':
        return `${result.name} — ${result.specialty || 'General'} — ${result.provider_type || 'Clinic'}`;
      case 'service_provider':
        return `${result.name} — ${result.provider_type || 'Hospital'} — ${result.location || ''}`;
      case 'specialty':
        return `${result.name} — Specialty`;
      default:
        return result.name || 'Unknown';
    }
  };

  // Helper function to get result type label
  const getResultTypeLabel = (resultType) => {
    switch (resultType) {
      case 'doctor':
        return 'Doctor';
      case 'service_provider':
        return 'Hospital/Clinic';
      case 'specialty':
        return 'Specialty';
      default:
        return 'Other';
    }
  };

  // Handle search button click
  const handleSearch = async () => {
    const query = searchValue || inputValue;
    if (!query) return;

    setHasSearched(true);
    await performSearch(query);
    
    if (onSearch) {
      onSearch(query, searchResults);
    }
  };

  // Handle result selection
  const handleResultSelect = (selectedValue) => {
    if (!selectedValue) return;

    if (onResultSelect) {
      onResultSelect(selectedValue);
    } else {
      // Default navigation behavior
      switch (selectedValue.result_type) {
        case 'doctor':
          navigate(`/doctors/${selectedValue.id}`);
          break;
        case 'service_provider':
          navigate(`/hospitals/${selectedValue.id}`);
          break;
        case 'specialty':
          navigate(`/find-doctors?specialty=${selectedValue.name}`);
          break;
        default:
          console.log('Unknown result type:', selectedValue.result_type);
      }
    }
  };

  // Handle Enter key press
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Test function to manually set results (for debugging)
 

  return (
    <Container
      maxWidth={maxWidth}
      sx={{
       mt: 2,
        justifyContent: 'space-between',
        mb: 2,
        
      
       
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border:`2px solid ${theme.palette.primary.main}`,
          borderRadius: { xs: '100px', md: '12px' },
          flexDirection: { xs: 'column', sm: 'row' },
          maxWidth: { md: '85%', lg: '95%', xl: '80%' },
          ml: { xs: 0, md: 0, lg: 4, xl: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            // ml: { xs: 0, md: 0, lg: 2, xl: 4 },
            padding: 0,
            flex: 1,
            width: { xs: '100%', sm: 'auto', md: 220, lg: 240, xl: 500 },
            borderRadius: { xs: '100px', md: '12px' },
            display: 'flex',
            alignItems: 'center',
            border:`2px solid ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: { md: '0 2px 8px rgba(0,0,0,0.08)' },
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: { md: '0 4px 12px rgba(0,0,0,0.12)' },
              borderColor: theme.palette.primary.main,
            },
            '&:focus-within': {
              boxShadow: { md: '0 4px 12px rgba(0,0,0,0.12)' },
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <Autocomplete
            freeSolo
            options={searchResults}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            value={searchValue}
            onChange={(_, newValue) => {
              setSearchValue(newValue);
              setIsOpen(false);
              if (newValue && typeof newValue === 'object' && newValue.id) {
                handleResultSelect(newValue);
              }
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
              setIsOpen(newInputValue.length >= 3);
            }}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            loading={isLoading}
            open={isOpen && searchResults.length > 0}
            sx={{ flex: 1, padding: '0px !important' }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                variant="outlined"
                autoComplete="off"
                onKeyDown={handleKeyDown}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  p: 0,
                  m: 0,
                  '& .MuiInputBase-root': {
                    p: '0px !important',
                    m: '0px !important',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '1em', md: '0.8rem' },
                    padding: '0px !important',
                    ml: 2,
                    fontWeight: 600,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                    padding: '0px !important',

                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: { xs: '1rem', md: '1rem' },
                    fontWeight: 600,
                    padding: '0px !important',
                    opacity: 0.8,
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={`${option.result_type}-${option.id}`}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    {showResultTypes && (
                      <Chip
                        label={option.displayType}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {option.specialty && `${option.specialty} • `}
                      {option.provider_type && `${option.provider_type} • `}
                      {option.location}
                      {option.consultation_fee && ` • ₹${option.consultation_fee}`}
                      {option.rating && ` • ⭐ ${option.rating}`}
                    </Typography>
                  </Box>
                  {option.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5, fontStyle: 'italic' }}>
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            noOptionsText={
              error ? (
                <Typography color="error" sx={{ p: 2 }}>
                  {error}
                </Typography>
              ) : hasSearched && !isLoading ? (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                  No results found
                </Typography>
              ) : (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                  Type at least 3 characters to search...
                </Typography>
              )
            }
          />
          <IconButton
            sx={{
              p: { xs: 1.5, md: 2 },
              mr: { xs: 0.5, md: 1 },
              color: theme.palette.primary.main,
            }}
            onClick={handleSearch}
            disabled={isLoading}
          >
            <SearchIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
          </IconButton>
          {/* Temporary test button - remove after debugging */}
        
        </Paper>
      </Box>
    </Container>
  );
};

export default CommonSearch;
